// Разговор с агентом: лента (рассказ → шаги → сырые данные по требованию) и поле ввода.
// Используется и в выезжающей панели, и на странице «Агент → Чаты».
import {
  type AgentTurn,
  type Block,
  describeTool,
  effortLabel,
  modelLabel,
  type SystemTurn,
  type ThreadView,
  type UserTurn,
} from '@autoaff/protocol';
import { duration, inTime, toast, usd } from '@autoaff/ui';
import {
  ArrowUp,
  Check,
  ChevronDown,
  CircleAlert,
  ClipboardList,
  Cpu,
  FilePen,
  FilePlus,
  FileText,
  FolderSearch,
  Globe,
  Info,
  Link2,
  ListChecks,
  Mic,
  Paperclip,
  Search,
  ShieldQuestion,
  Square,
  Terminal,
  TriangleAlert,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api } from '../api';
import { ComposerAttachments, MessageAttachments, useAttachments, useDropFiles } from './Attachments';
import type { AttachmentRef } from './attach';
import { useVoiceInput } from './useVoice';

const TOOL_ICON: Record<string, typeof Wrench> = {
  Read: FileText,
  Write: FilePlus,
  Edit: FilePen,
  MultiEdit: FilePen,
  Grep: Search,
  Glob: FolderSearch,
  Bash: Terminal,
  WebSearch: Globe,
  WebFetch: Link2,
  Task: Users,
  Agent: Users,
  TodoWrite: ListChecks,
};

const Md = memo(function Md({ text }: { text: string }) {
  return (
    <div className="md">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{ a: ({ node: _n, ...p }) => <a {...p} target="_blank" rel="noreferrer" /> }}
      >
        {text}
      </Markdown>
    </div>
  );
});

function StepRow({ block }: { block: Extract<Block, { kind: 'tool' }> }) {
  const [open, setOpen] = useState(false);
  const Icon = TOOL_ICON[block.name] ?? Wrench;
  const d = describeTool(block.name, block.input, block.status === 'running' ? 'running' : 'done');
  const state =
    block.status === 'running' ? (
      <span className="spinner" role="img" aria-label="Выполняется" />
    ) : block.status === 'done' ? (
      <Check aria-label="Готово" style={{ color: 'var(--success)' }} />
    ) : block.status === 'denied' ? (
      <X aria-label="Отказано" style={{ color: 'var(--warning)' }} />
    ) : block.status === 'interrupted' ? (
      <Square aria-label="Прервано" style={{ color: 'var(--muted)' }} />
    ) : (
      <CircleAlert aria-label="Ошибка" style={{ color: 'var(--danger)' }} />
    );
  return (
    <div className="step">
      <span className="step-ico">
        <Icon />
      </span>
      <div className="step-main">
        <b>{d.title}</b>
        {d.detail && <span className="detail">{d.detail}</span>}
        {open && block.output && <pre className="step-out">{block.output}</pre>}
      </div>
      {block.output && (
        <button
          type="button"
          className="icon-btn"
          style={{ width: 24, height: 24 }}
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Скрыть вывод' : 'Показать вывод'}
          aria-expanded={open}
        >
          <ChevronDown style={{ width: 14, height: 14, transform: open ? 'rotate(180deg)' : undefined }} />
        </button>
      )}
      <span className="step-state">{state}</span>
    </div>
  );
}

function PermissionCard({ block }: { block: Extract<Block, { kind: 'permission' }> }) {
  const [busy, setBusy] = useState<'allow' | 'deny' | null>(null);
  const d = describeTool(block.toolName, block.input, 'request');
  const done = block.status !== 'pending';
  const detail =
    typeof block.input.command === 'string'
      ? block.input.command
      : typeof block.input.content === 'string'
        ? `${String(block.input.file_path ?? '')}\n\n${block.input.content.slice(0, 1200)}`
        : JSON.stringify(block.input, null, 2);
  const decide = async (decision: 'allow' | 'deny') => {
    setBusy(decision);
    try {
      await api(`/api/permissions/${block.requestId}`, { decision });
    } catch (e) {
      toast((e as Error).message);
      setBusy(null);
    }
  };
  const verdict =
    block.status === 'allow'
      ? 'Вы разрешили'
      : block.status === 'deny'
        ? 'Вы отказали'
        : block.status === 'expired'
          ? 'Время на ответ вышло — агент продолжил без этого'
          : null;
  return (
    <div className={`perm ${done ? 'done' : ''}`}>
      <div className="perm-title">
        <ShieldQuestion />
        <span>
          {done
            ? `${d.title}${d.detail ? ` · ${d.detail}` : ''}`
            : `Агент просит разрешения: ${d.title.toLowerCase()}`}
        </span>
      </div>
      {!done && <pre>{detail}</pre>}
      {done ? (
        <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
          {verdict}
        </span>
      ) : (
        <div className="perm-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy !== null}
            onClick={() => decide('allow')}
          >
            {busy === 'allow' ? <span className="spinner" /> : <Check />}
            Разрешить
          </button>
          <button type="button" className="btn" disabled={busy !== null} onClick={() => decide('deny')}>
            Отказать
          </button>
          <span className="muted" style={{ fontSize: 'var(--fs-xs)', marginLeft: 'auto' }}>
            ответ нужен {inTime(block.expiresAt)}
          </span>
        </div>
      )}
    </div>
  );
}

function Notice({ level, text }: { level: 'info' | 'warning' | 'error'; text: string }) {
  const Icon = level === 'error' ? CircleAlert : level === 'warning' ? TriangleAlert : Info;
  return (
    <div className={`notice ${level}`}>
      <Icon />
      <span>{text}</span>
    </div>
  );
}

function AgentTurnView({ turn, partial, live }: { turn: AgentTurn; partial: string | null; live: boolean }) {
  const tools = turn.blocks.filter((b): b is Extract<Block, { kind: 'tool' }> => b.kind === 'tool');
  const [showSteps, setShowSteps] = useState(false);
  const working = turn.status === 'working';
  const stepsVisible = working || showSteps;
  const failed = tools.filter((t) => t.status === 'error').length;

  // Порядок подачи: рассказ агента, шаги свёрнуты в строку, по клику — подробности
  const body: React.ReactNode[] = [];
  for (const b of turn.blocks) {
    if (b.kind === 'text') body.push(<Md key={b.key} text={b.text} />);
    else if (b.kind === 'permission') body.push(<PermissionCard key={b.key} block={b} />);
    else if (b.kind === 'notice') body.push(<Notice key={b.key} level={b.level} text={b.text} />);
  }
  return (
    <div className="msg-agent">
      {tools.length > 0 &&
        (stepsVisible ? (
          <div className="steps">
            {!working && (
              <button
                type="button"
                className="steps-toggle"
                aria-expanded="true"
                onClick={() => setShowSteps(false)}
              >
                <ListChecks />
                Скрыть шаги
                <ChevronDown />
              </button>
            )}
            {tools.map((b) => (
              <StepRow key={b.key} block={b} />
            ))}
          </div>
        ) : (
          <button
            type="button"
            className="steps-toggle"
            aria-expanded="false"
            onClick={() => setShowSteps(true)}
          >
            {failed ? <CircleAlert style={{ color: 'var(--danger)' }} /> : <Check />}
            {tools.length} {tools.length === 1 ? 'шаг' : tools.length < 5 ? 'шага' : 'шагов'}
            {failed ? ` · ${failed} с ошибкой` : ''}
            <ChevronDown />
          </button>
        ))}
      {body}
      {partial !== null && (
        <div className="md">
          <Markdown remarkPlugins={[remarkGfm]}>{partial}</Markdown>
          <span className="caret" />
        </div>
      )}
      {!working && (
        <div className="msg-meta">
          {turn.status === 'stopped' && <span style={{ color: 'var(--warning)' }}>Остановлено вами</span>}
          {turn.status === 'interrupted' && <span style={{ color: 'var(--warning)' }}>Прервано</span>}
          {turn.model && <span>{modelLabel(turn.model)}</span>}
          {turn.durationMs !== undefined && <span>{duration(turn.durationMs)}</span>}
          {turn.costUsd !== undefined && turn.costUsd > 0 && (
            <span title="Условная стоимость по прайсу API; оплачивается подпиской">
              ≈ {usd(turn.costUsd, 3)}
            </span>
          )}
        </div>
      )}
      {live && working && partial === null && <span className="sr-only">агент работает</span>}
    </div>
  );
}

function SystemTurnView({ turn }: { turn: SystemTurn }) {
  return (
    <div className="msg-sys" role="note">
      <Cpu aria-hidden />
      <span>
        Дальше — {modelLabel(turn.model)}
        {turn.effort ? `, глубина ${effortLabel(turn.effort).toLowerCase()}` : ''}
      </span>
    </div>
  );
}

function workingText(v: ThreadView): string {
  const last = v.turns[v.turns.length - 1];
  if (v.waiting) return 'Ждёт вашего решения';
  if (last?.kind === 'agent' && last.status === 'working') {
    const running = [...last.blocks].reverse().find((b) => b.kind === 'tool' && b.status === 'running');
    if (running && running.kind === 'tool')
      return `${describeTool(running.name, running.input, 'running').title}…`;
  }
  if (v.partial) return 'Пишет ответ…';
  return 'Думает…';
}

/** Что делает агент прямо сейчас; на долгом шаге — сколько секунд он уже идёт (на высокой глубине Opus думает секундами). */
function WorkingLine({ text }: { text: string }) {
  const [since] = useState(() => Date.now());
  const [now, setNow] = useState(since);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const s = Math.floor((now - since) / 1000);
  return (
    <div className="working" aria-live="polite">
      <span className="spinner" />
      {s >= 3 ? text.replace(/…$/, '') : text}
      {s >= 3 && (
        <span className="dim tnum" aria-hidden>
          · {s} с
        </span>
      )}
    </div>
  );
}

export function Conversation({
  view,
  ready,
  empty,
}: {
  view: ThreadView;
  ready: boolean;
  empty?: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  });
  // Высота меняется и без новой отрисовки (догрузились шрифты, раскрылся блок, выросло поле ввода) —
  // лента остаётся прижатой к последнему сообщению
  useEffect(() => {
    const el = ref.current;
    const inner = el?.firstElementChild;
    if (!el || !inner) return;
    const ro = new ResizeObserver(() => {
      if (stick.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(inner);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const onScroll = () => {
    const el = ref.current;
    if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  const lastIdx = view.turns.length - 1;
  const last = view.turns[lastIdx];
  // Недописанный текст принадлежит последнему ходу агента, если он ещё идёт; иначе — новый сегмент ответа
  const partialInLast = view.partial !== null && last?.kind === 'agent' && last.status === 'working';

  return (
    <div className="convo" ref={ref} onScroll={onScroll}>
      <div className="convo-inner">
        {!ready && <ConvoSkeleton />}
        {ready && view.turns.length === 0 && view.queued.length === 0 && !view.partial && empty}
        {view.turns.map((t, i) =>
          t.kind === 'user' ? (
            <UserMessage key={t.key} turn={t} />
          ) : t.kind === 'system' ? (
            <SystemTurnView key={t.key} turn={t} />
          ) : (
            <AgentTurnView
              key={t.key}
              turn={t}
              live={i === lastIdx}
              partial={i === lastIdx && partialInLast && view.partial ? view.partial.text : null}
            />
          ),
        )}
        {view.partial && !partialInLast && (
          <div className="msg-agent">
            <div className="md">
              <Markdown remarkPlugins={[remarkGfm]}>{view.partial.text}</Markdown>
              <span className="caret" />
            </div>
          </div>
        )}
        {view.queued.map((q) => (
          <UserMessage
            key={q.key}
            turn={q}
            note={view.working ? 'Агент прочтёт после текущего шага' : 'Отправляется…'}
          />
        ))}
        {view.working && !view.partial && <WorkingLine key={workingText(view)} text={workingText(view)} />}
      </div>
    </div>
  );
}

/** Первая строка задания по существу: без служебной «[Контекст: …]», коротко. */
function briefPreview(text: string): string {
  const line = text
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l && !l.startsWith('[Контекст'));
  if (!line) return '';
  return line.length > 90 ? `${line.slice(0, 89)}…` : line;
}

/**
 * Задание фоновой работы (повод, брифинг с цифрами, продолжение) пишет платформа, а не владелец, и оно длинное —
 * в ленте оно свёрнуто в одну строку, полностью — по нажатию (рассказ → цифры → сырые данные).
 */
function TaskBrief({ turn }: { turn: UserTurn }) {
  const [open, setOpen] = useState(false);
  const preview = briefPreview(turn.text);
  return (
    <div className="msg-brief">
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}>
        <ClipboardList aria-hidden="true" />
        <b>Задание агенту</b>
        {preview && <span>{preview}</span>}
        <ChevronDown aria-hidden="true" />
      </button>
      {open && <div className="msg-brief-text">{turn.text}</div>}
    </div>
  );
}

/** Сообщение владельца: вложения (миниатюры, карточки) над текстом; ещё не прочитанное — с пометкой. */
function UserMessage({ turn, note }: { turn: UserTurn; note?: string }) {
  if (turn.background) return <TaskBrief turn={turn} />;
  const bubble =
    turn.text || note ? (
      <div className={`msg-user${note ? ' queued' : ''}`}>
        {turn.text}
        {note && <span className="q">{note}</span>}
      </div>
    ) : null;
  if (!turn.attachments?.length) return bubble;
  return (
    <div className="msg-user-group">
      <MessageAttachments items={turn.attachments} />
      {bubble}
    </div>
  );
}

function ConvoSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} aria-hidden>
      <div
        className="skeleton"
        style={{ alignSelf: 'flex-end', width: '55%', height: 40, borderRadius: 16 }}
      />
      <div className="skeleton" style={{ width: '85%', height: 14 }} />
      <div className="skeleton" style={{ width: '70%', height: 14 }} />
      <div className="skeleton" style={{ width: '40%', height: 14 }} />
    </div>
  );
}

export function Composer({
  working,
  disabled,
  onSend,
  onStop,
  onFocusInput,
  tools,
  autoFocus,
  placeholder,
}: {
  working: boolean;
  disabled?: boolean;
  /** Своя подсказка в пустом поле (например, «Ваш ответ агенту…», когда ждут ответа на его вопрос). */
  placeholder?: string;
  /** Отправить текст и вложения; false — не ушло (текст и файлы вернутся в поле). */
  onSend: (text: string, attachments: AttachmentRef[]) => Promise<boolean | undefined> | boolean | undefined;
  onStop: () => void;
  /** Фокус в поле ввода — сигнал поднять процесс агента заранее. */
  onFocusInput?: () => void;
  /** Дополнительные кнопки в строке под полем (выбор модели). */
  tools?: React.ReactNode;
  autoFocus?: boolean;
}) {
  const [text, setText] = useState('');
  const ta = useRef<HTMLTextAreaElement>(null);
  const form = useRef<HTMLFormElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocus) ta.current?.focus({ preventScroll: true });
  }, [autoFocus]);
  // Вложения: скриншот из буфера (Ctrl+V), файлы, перетащенные на разговор, и скрепка (на телефоне — галерея, камера)
  const att = useAttachments();
  const [sending, setSending] = useState(false);
  const veil = useDropFiles(
    () => form.current?.parentElement,
    (files) => att.add(files),
  );
  const hasFiles = att.items.length > 0;
  // Голос: запись → расшифровка (Gemini) → текст в поле; отправляет владелец сам, поправив, если нужно
  const v = useVoiceInput((heard) => {
    setText((t) => (t.trim() ? `${t.trimEnd()} ${heard}` : heard));
    ta.current?.focus({ preventScroll: true });
  });
  const voice = v.state;
  const secs = v.secs;
  const toggleVoice = v.toggle;
  const cancelVoice = v.cancel;
  // biome-ignore lint/correctness/useExhaustiveDependencies: высоту поля пересчитываем при каждом изменении текста
  useLayoutEffect(() => {
    const el = ta.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [text]);
  const submit = async () => {
    const t = text.trim();
    if ((!t && !hasFiles) || disabled || sending) return;
    let refs: AttachmentRef[] = [];
    if (hasFiles) {
      // Файлы ещё грузятся — дождёмся и отправим сразу за ними
      setSending(true);
      try {
        refs = await att.ready();
      } catch (e) {
        toast((e as Error).message);
        return;
      } finally {
        setSending(false);
      }
    }
    // Пока файлы догружались, владелец мог дописать текст или убрать файлы
    const final = (ta.current?.value ?? t).trim();
    if (!final && !refs.length) return;
    const taken = att.take();
    setText('');
    if ((await onSend(final, refs)) === false) {
      taken.restore();
      setText((cur) => cur || final);
    } else taken.drop();
  };
  return (
    <form
      ref={form}
      className="composer"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {veil}
      <div className="cbox">
        {hasFiles && <ComposerAttachments items={att.items} onRemove={att.remove} />}
        <textarea
          ref={ta}
          rows={1}
          value={text}
          id="chat-input"
          aria-label="Сообщение агенту"
          placeholder={
            voice === 'rec'
              ? 'Говорите… нажмите на микрофон ещё раз, чтобы закончить'
              : voice === 'busy'
                ? 'Расшифровываю голосовое…'
                : placeholder
                  ? placeholder
                  : working
                    ? 'Дописать агенту — он прочтёт после текущего шага…'
                    : hasFiles
                      ? 'Напишите, что сделать с файлами, — или отправьте так'
                      : 'Спросите агента или поставьте задачу…'
          }
          onChange={(e) => setText(e.target.value)}
          onPaste={(e) => {
            // Скриншот или картинка из буфера — во вложения; скопированный текст (и ячейки таблицы, у которых в
            // буфере есть ещё и картинка) — как текст. Скопированный в проводнике файл несёт текстом своё имя
            const files = [...e.clipboardData.files];
            const plain = e.clipboardData.getData('text/plain').trim();
            if (!files.length || (plain && !files.some((f) => f.name && plain.endsWith(f.name)))) return;
            e.preventDefault();
            att.add(files, { pasted: true });
          }}
          onFocus={onFocusInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void submit();
            } else if (e.key === 'Escape' && working) {
              e.preventDefault();
              onStop();
            }
          }}
        />
        <div className="cbar">
          <button
            type="button"
            className="icon-btn"
            aria-label="Приложить файлы"
            title="Приложить файлы — или вставьте скриншот: Ctrl+V"
            onClick={() => picker.current?.click()}
          >
            <Paperclip />
          </button>
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            tabIndex={-1}
            accept="image/*,video/*,audio/*,.heic,.heif,.pdf,.txt,.csv,.tsv,.json,.md,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.zip"
            onChange={(e) => {
              att.add([...(e.target.files ?? [])]);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className={`icon-btn ${voice === 'rec' ? 'mic-rec' : ''}`}
            aria-label={voice === 'rec' ? 'Закончить запись и расшифровать' : 'Сказать голосом'}
            aria-pressed={voice === 'rec'}
            title={voice === 'rec' ? 'Закончить запись' : 'Сказать голосом'}
            disabled={voice === 'busy'}
            onClick={toggleVoice}
          >
            {voice === 'busy' ? <span className="spinner" /> : <Mic />}
          </button>
          {voice === 'rec' && (
            <>
              <span className="rec-time tnum" aria-live="polite">
                {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}
              </span>
              <button
                type="button"
                className="icon-btn"
                aria-label="Бросить запись"
                title="Бросить запись"
                onClick={cancelVoice}
              >
                <X />
              </button>
            </>
          )}
          {tools}
          {/* Остановить — одна кнопка, всегда на виду, пока агент работает: даже когда владелец печатает поправку.
              Кнопка отправки остаётся отправкой (стрелка), не превращается во второй «стоп». */}
          {working && (
            <button type="button" className="btn btn-sm btn-ghost stop-now" onClick={onStop}>
              <Square />
              Остановить
            </button>
          )}
          <button
            type="submit"
            className="send"
            aria-label={sending ? 'Загружаю файлы и отправлю' : working ? 'Дописать агенту' : 'Отправить'}
            title={
              sending ? 'Загружаю файлы — отправлю сразу за ними' : working ? 'Дописать агенту' : 'Отправить'
            }
            disabled={(!text.trim() && !hasFiles) || disabled || sending}
          >
            {sending ? <span className="spinner" /> : <ArrowUp />}
          </button>
        </div>
      </div>
    </form>
  );
}
